/**
 * LES DEUX DÉTECTEURS DE FRICTION (DEV-1508 lot 2, contrat v1.0 §3.1).
 * ============================================================================
 *
 * INTENTION : savoir où la page résiste, sans espionner les gestes. Sentry voit
 * déjà les rages et les clics morts, mais sur 20 % des sessions seulement : notre
 * détecteur est la source de COMPTAGE, il couvre tout le monde.
 *
 * Deux frictions, et seulement deux :
 *  • RAGE_CLIC : une rafale de clics sur la même cible. Quelqu'un insiste parce
 *    que rien ne se passe ;
 *  • CLIC_MORT : un clic qui n'a rien changé. Quelque chose a l'air cliquable et
 *    ne l'est pas.
 *
 * Trois règles :
 *  1. UN ÉVÉNEMENT PAR RAFALE, jamais par clic. C'est l'exception assumée au
 *    « pas de clics individuels » du plan de suivi : elle reste une exception,
 *    la rafale entière ne produit qu'une ligne ;
 *  2. la CIBLE est un identifiant STABLE de l'élément (`onglet:puissance`,
 *    `bouton:revenir-au-contexte`), jamais un texte saisi ni une valeur lue ;
 *  3. aucune donnée de mesure ne sort d'ici : un compte, un type, une cible.
 *
 * Le module est pur : il ne lit ni l'horloge ni le DOM, on lui passe l'instant.
 * C'est ce qui le rend testable sans faux temps ni faux clics.
 */

/** Durée d'une rafale : au delà, les clics suivants forment une autre rafale. */
export const RAFALE_MS = 1000

/** Nombre de clics qui fait une rage. Le troisième la déclare. */
export const CLICS_RAGE = 3

export type FrictionDetectee = {
    type: "rage_clic" | "clic_mort"
    cible: string
    nb_clics: number
}

export type DetecteurFriction = {
    /**
     * Un clic vient d'avoir lieu.
     *
     * @param cible - identifiant stable de l'élément
     * @param maintenant - l'instant du clic (ms)
     * @param aEuUnEffet - `false` quand le clic n'a RIEN changé à l'écran
     */
    clic: (cible: string, maintenant: number, aEuUnEffet?: boolean) => void
    /** Remet les rafales à zéro (démontage de la page). */
    reinitialiser: () => void
}

type Rafale = {
    cible: string
    debut: number
    dernier: number
    clics: number
    /** La rage a déjà été émise pour cette rafale : on ne la répète pas. */
    rageEmise: boolean
    /** Le clic mort a déjà été émis pour cette rafale. */
    mortEmis: boolean
}

/**
 * Un détecteur, qui appelle `emettre` une fois par friction et par rafale.
 *
 * ⚠️ `emettre` ne doit jamais lever : le suivi ne casse pas la page. L'appel est
 * quand même encadré ici, parce qu'un détecteur qui tue l'écran serait pire que
 * pas de détecteur du tout.
 */
export function creerDetecteurFriction(
    emettre: (friction: FrictionDetectee) => void,
    options?: { rafaleMs?: number; clicsRage?: number }
): DetecteurFriction {
    const rafaleMs = options?.rafaleMs ?? RAFALE_MS
    const clicsRage = options?.clicsRage ?? CLICS_RAGE
    let rafale: Rafale | null = null

    function signaler(friction: FrictionDetectee): void {
        try {
            emettre(friction)
        } catch {
            /* le suivi ne casse jamais la page */
        }
    }

    return {
        clic(cible, maintenant, aEuUnEffet = true) {
            const memeRafale =
                rafale !== null &&
                rafale.cible === cible &&
                maintenant - rafale.dernier <= rafaleMs

            rafale = memeRafale
                ? {
                      ...(rafale as Rafale),
                      dernier: maintenant,
                      clics: (rafale as Rafale).clics + 1,
                  }
                : {
                      cible,
                      debut: maintenant,
                      dernier: maintenant,
                      clics: 1,
                      rageEmise: false,
                      mortEmis: false,
                  }

            if (!aEuUnEffet && !rafale.mortEmis) {
                rafale.mortEmis = true
                signaler({ type: "clic_mort", cible, nb_clics: rafale.clics })
            }

            if (rafale.clics >= clicsRage && !rafale.rageEmise) {
                rafale.rageEmise = true
                signaler({ type: "rage_clic", cible, nb_clics: rafale.clics })
            }
        },
        reinitialiser() {
            rafale = null
        },
    }
}
